/* eslint-disable no-use-before-define */
export const formatFullyQualifiedUrl = (url: string) => {
  if (url.startsWith("http")) {
    return url;
  }

  // If the url starts with a slash (but not a double slash)
  if (/^\/[^/]+/.test(url)) {
    return `${location.protocol}//${location.host}${url}`;
  }

  return `${location.protocol}${url}`;
};

export const getFullyQualifiedUrl = (
  url: string,
  searchParams: Record<string, string> = {}
) => {
  const fullyQualifiedUrl = new URL(formatFullyQualifiedUrl(url));
  for (const key in searchParams) {
    fullyQualifiedUrl.searchParams.append(key, searchParams[key]);
  }
  return fullyQualifiedUrl;
};

export const stringToHTML = function (str: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, "text/html");
  return doc.body;
};

/**
 * Decodes HTML entities in a string
 * @param str - The string containing HTML entities to decode
 * @returns The decoded string
 * @example
 * decodeHTMLEntities("&lt;div&gt;Hello &amp; World&lt;/div&gt;")
 * // Returns: "<div>Hello & World</div>"
 */
export const decodeHTMLEntities = (str: string): string => {
  if (typeof document === "undefined") return str;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str;
  return textarea.value;
};

export const getLastFocusableElement = (element: HTMLElement) => {
  if (!element) return null;
  const focusElements = element.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  return focusElements[focusElements.length - 1];
};

export const getFirstFocusableElement = (element: HTMLElement) => {
  if (!element) return null;
  return element.querySelector<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
};

export const debounce = <T extends (...args: unknown[]) => unknown>(
  fn: T,
  wait: number
) => {
  let t: NodeJS.Timeout;
  const callable = (...args: unknown[]) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
  return <T>(<unknown>callable);
};

export const getShopifyGlobalId = (
  type: "Cart" | "Collection" | "Metaobject" | "Product" | "ProductVariant",
  reference: string | number
) =>
  reference.toString().includes(`gid://shopify/${type}`)
    ? reference.toString()
    : `gid://shopify/${type}/${reference}`;

export const getHeaderHeights = () => {
  const navBar = document.querySelector<HTMLElement>(
    "#shopify-section-tbsp-navigation"
  );
  const navBarHeight = navBar ? navBar.clientHeight : 0;
  const stickyBar = document.querySelector<HTMLElement>(
    ".tbsp-sticky-product-bar"
  );
  const stickyBarHeight = stickyBar ? stickyBar.clientHeight : 0;
  const heightsTotal = navBarHeight + stickyBarHeight;
  return heightsTotal;
};

export const formatKeyToLabel = (key: string, transformRules?: object) => {
  if (key === undefined || key === null) {
    return "N/A";
  }
  if (transformRules) {
    Object.entries(transformRules).forEach(([oldVal, newVal]) => {
      key = key.replaceAll(oldVal, newVal);
    });
  }
  const exemptFromCapitalization = ["of"];
  return key
    .split("_")
    .map((string) => {
      return exemptFromCapitalization.includes(string)
        ? string
        : string.charAt(0).toUpperCase() + string.slice(1);
    })
    .join("")
    .replace(/([A-Z])/g, " $1")
    .trim();
};

export const formatSnakeCaseToCamelCase = (key: string) => {
  return key
    .split("_")
    .map((string, index) => {
      return index > 0
        ? string.charAt(0).toUpperCase() + string.slice(1)
        : string;
    })
    .join("");
};

export const formatCamelCaseToSnakeCase = (key: string) => {
  return key
    .split("")
    .map((char, index) => {
      return char === " "
        ? ""
        : char === char.toUpperCase()
        ? `${index === 0 ? "" : "_"}${char.toLowerCase()}`
        : char;
    })
    .join("");
};

export const checkIfHeightString = (word: string) => {
  return /\d+'\s?\d+"/.test(word);
};

export const getInchValueFromHeightString = (heightString: string) => {
  const heightPortion = heightString.match(/\d+'/);
  const inchPortion = heightString.match(/\d+"/);
  const heightVal =
    heightPortion && heightPortion.index !== undefined
      ? heightString.slice(heightPortion.index, heightString.indexOf(`'`))
      : "";
  const inchVal =
    inchPortion && inchPortion.index !== undefined
      ? heightString.slice(inchPortion.index, heightString.indexOf(`"`))
      : "";
  return Number(heightVal) * 12 + Number(inchVal);
};

export const compensateADVisibility = (
  node: HTMLElement,
  visibility: boolean
) => {
  const parentElement = node.parentElement;
  if (!parentElement) return;

  if (visibility) {
    Array.from(parentElement.children).forEach((element) => {
      element.setAttribute("aria-hidden", "true");
    });
    node.removeAttribute("aria-hidden");
  } else {
    Array.from(parentElement.children).forEach((element) => {
      element.setAttribute("aria-hidden", "true");
    });
    document
      ?.querySelector("[data-focus-trap-default]")
      ?.removeAttribute("aria-hidden");
  }
};

/**
 * Like Object.keys, but unsound in exchange for more convenience.
 *
 * Casts the result of Object.keys to the known keys of an object type,
 * even though JavaScript objects may contain additional keys.
 *
 * Only use this function when you know/control the provenance of the object
 * you're iterating, and can verify it contains exactly the keys declared
 * to the type system.
 *
 * Example:
 * ```
 * const o = {x: "ok", y: 10}
 * o["z"] = "UNTRACKED_KEY"
 * const safeKeys = Object.keys(o)
 * const unsafeKeys = objectKeys(o)
 * ```
 * => const safeKeys: string[]
 * => const unsafeKeys: ("x" | "y")[] // Missing "z"
 */
export const ObjectKeys = Object.keys as <T>(obj: T) => Array<keyof T>;

/**
 * The type of a single item in `Object.entries<T>(value: T)`.
 *
 * Example:
 * ```
 * interface T {x: string; y: number}
 * type T2 = ObjectEntry<T>
 * ```
 * => type T2 = ["x", string] | ["y", number]
 */
export type ObjectEntry<T> = {
  // Without Exclude<keyof T, undefined>, this type produces `ExpectedEntries | undefined`
  // if T has any optional keys.
  [K in Exclude<keyof T, undefined>]: [K, T[K]];
}[Exclude<keyof T, undefined>];

/**
 * Like Object.entries, but returns a more specific type which can be less safe.
 *
 * Example:
 * ```
 * const o = {x: "ok", y: 10}
 * const unsafeEntries = Object.entries(o)
 * const safeEntries = objectEntries(o)
 * ```
 * => const unsafeEntries: [string, string | number][]
 * => const safeEntries: ObjectEntry<{
 *   x: string;
 *   y: number;
 * }>[]
 *
 * See `ObjectEntry` above.
 *
 * Note that Object.entries collapses all possible values into a single union
 * while objectEntries results in a union of 2-tuples.
 */
export const ObjectEntries = Object.entries as <T>(
  o: T
) => Array<ObjectEntry<T>>;

/**
 * Validates if a string is a valid URL
 * @param url - The string to validate
 * @returns true if the string is a valid URL, false otherwise
 */
export const isValidUrl = (url: string): boolean => {
  if (!url || typeof url !== "string") {
    return false;
  }

  try {
    // For relative URLs (starting with /), consider them valid
    if (url.startsWith("/")) {
      return true;
    }

    // For absolute URLs, use URL constructor to validate
    const urlObj = new URL(url);
    return Boolean(urlObj);
  } catch {
    return false;
  }
};
